import React, { useEffect, useCallback, useRef } from 'react'
import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Target,
  ChevronLeft,
  ChevronRight,
  Calculator
} from 'lucide-react'
import { renderContent, sanitizeAnswerHTML } from '../utils/contentRenderer'
import { getAnswerValue } from '../utils/answerUtils'
import { useImageLightbox } from '../hooks/useImageLightbox'
import ImageLightbox from './ImageLightbox'
import AlternativeSolutionSection from './AlternativeSolutionSection'

const QuestionDetailModal = ({
  isOpen,
  onClose,
  question,
  questions = [],
  currentIndex = 0,
  onNavigate
}) => {
  const { lightboxState, closeLightbox, containerRef } = useImageLightbox(
    question?.question_id || question?.id
  )
  const explanationRef = useRef(null)
  const alternativeRef = useRef(null)
  const hasList = questions.length > 1 && onNavigate
  const canGoPrev = hasList && currentIndex > 0
  const canGoNext = hasList && currentIndex < questions.length - 1

  const handlePrev = useCallback(() => {
    if (canGoPrev) onNavigate(currentIndex - 1)
  }, [canGoPrev, currentIndex, onNavigate])

  const handleNext = useCallback(() => {
    if (canGoNext) onNavigate(currentIndex + 1)
  }, [canGoNext, currentIndex, onNavigate])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') handlePrev()
      else if (e.key === 'ArrowRight') handleNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handlePrev, handleNext])

  if (!isOpen || !question) return null

  // Normalize to a display string: answers may arrive as schema-v2 objects ({ value } / { values }),
  // which must never be rendered directly into JSX.
  const userAnswer = getAnswerValue(
    question.userAnswer || question.selectedAnswer || question.user_answer
  )
  const timeSpent = question.timeSpent || question.time_spent || 0
  const rationale = question.rationale || question.explanation || ''
  const hasAlternativeSolution = !!question.hasAlternativeSolution
  // The dedicated alternative-solution datapoint, rendered below as its own linkable section.
  const alternativeSolution = question.alternativeSolution || null
  const alternativeSolutionType =
    question.alternativeSolutionType ||
    (/^\*\*\s*Desmos Alternative/i.test(alternativeSolution || '') ? 'desmos' : 'method')

  const scrollToExplanation = () => {
    // Prefer the dedicated alternative section (that's what the banner refers to); fall back to the
    // explanation when a question is flagged but has no extracted alternative text (e.g. Desmos-only).
    const target = alternativeRef.current || explanationRef.current
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Parse answer options
  const answerOptions = question.answerOptions || question.answer_options || []

  // Determine correct answer from answer options (MCQ), else normalize the question's stored
  // correct_answer. Schema v2 stores it as an object ({ value } / { values }), which must be
  // resolved to a string before rendering — otherwise React throws on the raw object.
  const correctAnswerOption = answerOptions.find((option) => option.is_correct === true)
  const correctAnswer = correctAnswerOption
    ? String.fromCharCode(65 + answerOptions.indexOf(correctAnswerOption))
    : getAnswerValue(question.correct_answer) || null

  // Correctness of the STUDENT's answer. For MCQ the flagged answer option is the canonical source of
  // truth, so the header badge is derived from the same comparison the answer grid uses (student letter
  // vs the correct letter). This keeps the header and grid from ever contradicting each other — the
  // 102f13a9 bug, where a stale stored grade said "Answered Correctly" while the grid showed the
  // student's choice as wrong. Question types without options (SPR / free response) fall back to the
  // server-stored grade, which handles multi-value/normalized answers the client can't re-derive.
  const isCorrect =
    answerOptions.length > 0
      ? !!userAnswer && userAnswer === correctAnswer
      : (question.isCorrect ?? question.is_correct ?? false)

  // Format time
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}m ${secs}s`
  }

  // Get difficulty label
  const getDifficultyLabel = (difficulty) => {
    if (difficulty === 'H') return 'Hard'
    if (difficulty === 'M') return 'Medium'
    if (difficulty === 'E') return 'Easy'
    return difficulty
  }

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    if (difficulty === 'H') return 'bg-red-100 text-red-800'
    if (difficulty === 'M') return 'bg-yellow-100 text-yellow-800'
    if (difficulty === 'E') return 'bg-green-100 text-green-800'
    return 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div
              className={`p-2 rounded-full ${isCorrect ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}
            >
              {isCorrect ? (
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Question Details
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isCorrect ? 'Answered Correctly' : 'Answered Incorrectly'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-8" ref={containerRef}>
          {/* Alternative-solution notification: flags that this question can be solved a second way
              (beyond the traditional analytical approach) and, when there is a rationale to scroll
              to, offers a jump to where that solution is shown. */}
          {hasAlternativeSolution && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-indigo-800 dark:text-indigo-200">
                <Calculator className="w-5 h-5 flex-shrink-0" />
                <span>
                  This question has an alternative solution to the traditional (analytical)
                  approach.
                </span>
              </div>
              {rationale && (
                <button
                  onClick={scrollToExplanation}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-600 dark:bg-indigo-500 text-white hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors"
                >
                  Jump to solution
                </button>
              )}
            </div>
          )}

          {/* Question Metadata */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm">
              <Target className="w-4 h-4" />
              <span>ID: {question.question_id || question.questionId || question.id || 'N/A'}</span>
            </div>
            {question.domain && (
              <div className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm font-medium">
                {question.domain?.domain_name || question.domain}
              </div>
            )}
            {question.skill && (
              <div className="px-3 py-1.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded-full text-sm font-medium">
                {question.skill?.skill_name || question.skill}
              </div>
            )}
            {question.difficulty && (
              <div
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  question.difficulty === 'H'
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                    : question.difficulty === 'M'
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                      : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                }`}
              >
                {getDifficultyLabel(question.difficulty)}
              </div>
            )}
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-full text-sm">
              <Clock className="w-4 h-4" />
              <span>{formatTime(timeSpent)}</span>
            </div>
          </div>

          {/* Question Text */}
          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Question
            </h3>
            {question.context && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Context:
                </h4>
                <div className="question-content text-gray-800 dark:text-gray-200 max-w-none">
                  {renderContent(question.context, question.content_type || question.contentType)}
                </div>
              </div>
            )}
            {question.questionText && (
              <div className="question-content text-gray-800 dark:text-gray-200 max-w-none">
                {renderContent(
                  question.questionText,
                  question.content_type || question.contentType
                )}
              </div>
            )}
          </div>

          {/* Answer Options */}
          {answerOptions && answerOptions.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Answer Options
              </h3>
              <div className="space-y-3">
                {answerOptions.map((option, index) => {
                  const optionLetter = String.fromCharCode(65 + index) // A, B, C, D
                  const isUserAnswer = userAnswer === optionLetter
                  const isCorrectAnswer = correctAnswer === optionLetter

                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${
                        isCorrectAnswer
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-600'
                          : isUserAnswer && !isCorrectAnswer
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-600'
                            : 'bg-white dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                            isCorrectAnswer
                              ? 'bg-green-500 dark:bg-green-600 text-white'
                              : isUserAnswer && !isCorrectAnswer
                                ? 'bg-red-500 dark:bg-red-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {optionLetter}
                        </div>
                        <div className="flex-1">
                          <div className="question-content text-gray-800 dark:text-gray-200 max-w-none">
                            {renderContent(
                              option.content || option.text || option,
                              question.content_type || question.contentType,
                              sanitizeAnswerHTML
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-3">
                            {isCorrectAnswer && (
                              <span className="inline-flex items-center text-sm text-green-700 dark:text-green-400 font-medium">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Correct Answer
                              </span>
                            )}
                            {isUserAnswer && (
                              <span
                                className={`inline-flex items-center text-sm font-medium ${
                                  isCorrectAnswer
                                    ? 'text-green-700 dark:text-green-400'
                                    : 'text-red-700 dark:text-red-400'
                                }`}
                              >
                                <AlertCircle className="w-4 h-4 mr-1" />
                                Your Answer
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* SPR Answer (no answer options): surface the answer BEFORE the explanation, so the
              section order matches MCQ (question → answer → explanation) instead of leaving the
              answer to the Summary at the very end. */}
          {answerOptions.length === 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Answer
              </h3>
              <div className="space-y-3">
                <div
                  className={`p-4 rounded-lg border-2 ${
                    isCorrect
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-600'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-600'
                  }`}
                >
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Your Answer</p>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {userAnswer || 'Not answered'}
                    </span>
                    <span
                      className={`inline-flex items-center text-sm font-medium ${
                        isCorrect
                          ? 'text-green-700 dark:text-green-400'
                          : 'text-red-700 dark:text-red-400'
                      }`}
                    >
                      {isCorrect ? (
                        <CheckCircle className="w-4 h-4 mr-1" />
                      ) : (
                        <AlertCircle className="w-4 h-4 mr-1" />
                      )}
                      {isCorrect ? 'Correct' : 'Incorrect'}
                    </span>
                  </div>
                </div>
                {!isCorrect && (
                  <div className="p-4 rounded-lg border-2 bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-600">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Correct Answer</p>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {correctAnswer || '—'}
                      </span>
                      <span className="inline-flex items-center text-sm font-medium text-green-700 dark:text-green-400">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Correct Answer
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rationale/Explanation */}
          {rationale && (
            <div
              ref={explanationRef}
              id="alt-solution-explanation"
              className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg p-6"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-300">
                  Explanation
                </h3>
                {hasAlternativeSolution && (
                  <span
                    title="Has an alternative solution to the traditional (analytical) approach"
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded text-xs"
                  >
                    <Calculator className="w-3 h-3" />
                    Alt solution
                  </span>
                )}
              </div>
              <div className="question-content text-primary-800 dark:text-primary-200 leading-relaxed max-w-none">
                {renderContent(rationale, question.content_type || question.contentType)}
              </div>
            </div>
          )}

          {/* Separate, linkable alternative-solution section */}
          {alternativeSolution && (
            <AlternativeSolutionSection
              ref={alternativeRef}
              id="alt-solution-section"
              alternativeSolution={alternativeSolution}
              type={alternativeSolutionType}
              contentType={question.content_type || question.contentType}
            />
          )}

          {/* Summary */}
          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Your Answer</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {userAnswer || 'Not answered'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Correct Answer</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">
                  {correctAnswer}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Time Spent</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {formatTime(timeSpent)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Result</p>
                <p
                  className={`text-lg font-bold ${isCorrect ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                >
                  {isCorrect ? 'Correct' : 'Incorrect'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <ImageLightbox
          src={lightboxState.src}
          alt={lightboxState.alt}
          isOpen={lightboxState.isOpen}
          onClose={closeLightbox}
        />

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {hasList && `Question ${currentIndex + 1} of ${questions.length}`}
          </div>
          <div className="flex items-center gap-2">
            {hasList && (
              <>
                <button
                  onClick={handlePrev}
                  disabled={!canGoPrev}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </button>
                <button
                  onClick={handleNext}
                  disabled={!canGoNext}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="px-6 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuestionDetailModal
